import { useCallback } from "react";

import { useSyncExternalStore } from "react";

export function dispatchStorageEvent(key: string, newValue: string | null) {
	window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

const setLocalStorageItem = <T extends any>(key: string, value: T) => {
	const stringifiedValue = JSON.stringify(value);
	window.localStorage.setItem(key, stringifiedValue);
	dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string) => {
	window.localStorage.removeItem(key);
	dispatchStorageEvent(key, null);
};

const getLocalStorageItem = <T extends any>(key: string, initialValue?: T) => {
	const value = window.localStorage.getItem(key);
	if (!value && initialValue) {
		setLocalStorageItem(key, initialValue);
		return window.localStorage.getItem(key);
	}
	return value;
};

const useLocalStorageSubscribe = (callback: () => void) => {
	window.addEventListener("storage", callback);
	return () => window.removeEventListener("storage", callback);
};

type UseLocalStorageProps<T extends any> = {
	key: string;
	initialValue?: T;
	// Defaults to true
	json?: boolean;
	onUpdate?: (value: T | undefined | null) => void;
};

export function useLocalStorage<T extends any>(props: UseLocalStorageProps<T>) {
	const { key, initialValue, json = true, onUpdate } = props;

	const parse = (v: string) => (json ? JSON.parse(v) : v);

	const store = useSyncExternalStore(useLocalStorageSubscribe, () => {
		const value = getLocalStorageItem(key, initialValue);
		onUpdate?.(value ? parse(value) : undefined);
		return value;
	});

	const setState = useCallback(
		(v: string | ((v: string) => string)) => {
			try {
				const nextState = typeof v === "function" ? v(store && parse(store)) : v;

				if (nextState === undefined || nextState === null) {
					removeLocalStorageItem(key);
				} else {
					setLocalStorageItem(key, nextState);
				}
			} catch (e) {
				console.warn(e);
			}
		},
		[key, store]
	);

	return [store ? parse(store) : initialValue, setState];
}
